/**
 * Composable for mapping BS Activity data to CMS Activity form
 */

import type { ActivityForm, Stage, StageTranslation, Translation } from '@/types/cms';
import { getAppLanguageLocales } from '@/helpers/ConceptHelper';

const mapTravelTypeToMode = (travelType: string | null | undefined): 'walk' | 'bike' | null => {
  if (!travelType) return null;
  const normalized = String(travelType).toLowerCase();
  if (normalized.includes('bike') || normalized.includes('cycl')) return 'bike';
  if (normalized.includes('walk') || normalized.includes('hik')) return 'walk';
  return null;
};

const buildStageTranslations = (
  languages: Array<{ shortName?: string; locale?: string } | string>,
  overrides: Partial<StageTranslation> = {},
): StageTranslation[] => {
  return languages.map((lang) => {
    const locale = typeof lang === 'string' ? lang : (lang.shortName || lang.locale || 'en');
    return {
      locale,
      title: overrides.title || '',
      description: overrides.description || '',
      featured_image_alt: overrides.featured_image_alt || '',
      from: overrides.from || '',
      to: overrides.to || '',
      location_label: overrides.location_label || '',
    };
  });
};

export const mapActivityDaysToStages = (
  days: any[],
  selectedActivity: any,
  languages: Array<{ shortName?: string; locale?: string } | string>,
  locationMap: Record<number, string> = {},
): Stage[] => {
  const defaultMode = mapTravelTypeToMode(selectedActivity?.travel_type ?? selectedActivity?.travelType);

  return days.map((day: any, index: number) => {
    const isStage = Boolean(day.is_stage ?? day.isStage ?? day.type === 'stage');
    const originName =
      day.origin?.name ||
      locationMap[day.originId] ||
      locationMap[day.origin_id] ||
      selectedActivity?.origin?.name ||
      '';
    const destinationName =
      day.destination?.name ||
      locationMap[day.destinationId] ||
      locationMap[day.destination_id] ||
      selectedActivity?.destination?.name ||
      '';
    const title = day.title || `Day ${day.order || index + 1}`;
    const description = day.description || '';
    const distanceKm = day.stageLength ?? day.stage_length ?? day.distance_km ?? null;

    const translations = buildStageTranslations(languages, {
      title,
      description,
      from: isStage ? originName : '',
      to: isStage ? destinationName : '',
      location_label: !isStage ? originName || destinationName : '',
    });

    return {
      id: `stage-${day.id || Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      order: day.order || index + 1,
      title,
      description,
      translations,
      extra_data: day.extra_data || [],
      featured_image: day.featured_image || '',
      gallery_text: day.gallery_text || '',
      is_stage: isStage,
      mode: isStage ? defaultMode : null,
      distance_km: isStage && distanceKm != null ? Number(distanceKm) : null,
    };
  });
};

export const mapActivityDataToForm = (
  activityData: any,
  form: ActivityForm,
  getTranslation: (locale: string) => Translation,
  options?: { skipTranslations?: boolean },
) => {
  // Basic information from BS Activity
  form.title = activityData.name || '';
  form.duration = activityData.total_days ? `${activityData.total_days} days` : '';
  form.general_location = activityData.origin?.name || '';
  form.to_location = activityData.destination?.name || '';
  form.from_location = activityData.origin?.name || '';
  form.general_country = activityData.origin?.country || '';
  form.featured_image = activityData.featured_image?.url || '';
  
  if (Array.isArray(activityData.gallery)) {
    form.gallery = activityData.gallery
      .map((item: any) => {
        if (typeof item === 'string') {
          return item;
        }
        return item?.url || item?.original_url || '';
      })
      .filter((url: string) => !!url);
  } else {
    form.gallery = [];
  }
  
  form.content = activityData.description || '';

  // Modality from BS Activity (group / self-guided)
  form.modality = activityData.modality || 'self-guided';
  form.transfer = activityData.transfer ?? null;
  form.transport = activityData.transport ?? null;

  // Included/Excluded information from BS Activity
  if (activityData.included_information?.description) {
    form.included_information = [activityData.included_information.description];
  } else {
    form.included_information = [''];
  }

  if (activityData.excluded_information?.description) {
    form.not_included_information = [activityData.excluded_information.description];
  } else {
    form.not_included_information = [''];
  }

  // Extra information from BS Activity
  if (activityData.extras_information?.description) {
    form.extra_information = activityData.extras_information.description;
  } else {
    form.extra_information = '';
  }

  // Before/After trip information from BS Activity
  if (activityData.before_information?.description) {
    form.additional_info = activityData.before_information.description;
  } else {
    form.additional_info = '';
  }

  if (activityData.after_information?.description) {
    form.after_info = activityData.after_information.description;
  } else {
    form.after_info = '';
  }

  // Itinerary from BS activity days (when present). Do not clear existing stages
  // when days are absent — CMS stage data may be mapped afterward in edit mode.
  const activityDays = activityData.activity_days || activityData.activityDays;
  if (activityDays && activityDays.length > 0) {
    form.stages = activityDays.map((day: any, index: number) => {
      const title = day.title || `Day ${day.order || index + 1}`;
      const description = day.description || '';
      const isStage = Boolean(day.is_stage ?? day.isStage ?? day.type === 'stage');
      const originName = day.origin?.name || '';
      const destinationName = day.destination?.name || '';
      const distanceKm = day.stageLength ?? day.stage_length ?? day.distance_km ?? null;
      const mode = isStage ? mapTravelTypeToMode(activityData.travel_type ?? activityData.travelType) : null;
      
      return {
        id: `stage-${day.id}`,
        order: day.order || index + 1,
        title: title,
        description: description,
        translations: buildStageTranslations(
          [{ shortName: 'en' }, { shortName: 'es' }, { shortName: 'nl' }],
          {
            title,
            description,
            from: isStage ? originName : '',
            to: isStage ? destinationName : '',
            location_label: !isStage ? originName || destinationName : '',
          },
        ),
        extra_data: day.extra_data || [],
        featured_image: day.featured_image || '',
        gallery_text: day.gallery_text || '',
        is_stage: isStage,
        mode,
        distance_km: isStage && distanceKm != null ? Number(distanceKm) : null,
      };
    });
  } else if (!options?.skipTranslations) {
    form.stages = [];
  }

  // Seed translation fields from BS activity (skipped in edit mode when CMS translations load next)
  if (!options?.skipTranslations) {
    const primaryLocale = getAppLanguageLocales()[0] || 'en';
    const defaultTranslation = primaryLocale ? getTranslation(primaryLocale) : getTranslation('en');
    defaultTranslation.title = activityData.name || defaultTranslation.title || '';
    defaultTranslation.slug = activityData.name?.toLowerCase().replace(/\s+/g, '-') || defaultTranslation.slug || '';
    defaultTranslation.seo_title = activityData.name || defaultTranslation.seo_title || '';
    defaultTranslation.seo_description = activityData.description?.substring(0, 160) || defaultTranslation.seo_description || '';
  }
  
  // Reset CMS-specific fields (will be populated from CMS API if exists)
  form.base_price = 0;
  form.base_price_single_room = 0;
  form.base_price_double_room = 0;
  form.transfer = activityData.transfer ?? null;
  form.transport = activityData.transport ?? null;
  form.group_size = 0;
  form.start_date = '';
  form.end_date = '';
  form.date_ranges = [];
  form.general_accomodation = '';
  form.categories = [];
  form.difficulty = 'medium';
  form.available_languages = [];
  form.type_of_booking = 'enquiry';
  form.guiders = [];
  form.reviews = [];
  form.faq = [];
};

export const mapBasicActivityData = (selectedActivity: any, form: ActivityForm) => {
  // Fallback mapping with basic data
  form.title = selectedActivity.name || '';
  form.duration = selectedActivity.total_days ? `${selectedActivity.total_days} days` : '';
  form.general_location = selectedActivity.origin?.name || '';
  form.to_location = selectedActivity.destination?.name || '';
  form.from_location = selectedActivity.origin?.name || '';
  form.transfer = selectedActivity.transfer ?? null;
  form.transport = selectedActivity.transport ?? null;
};
